import { _ as _extends } from '../../../../node_modules/.pnpm/@swc_helpers@0.5.15/node_modules/@swc/helpers/esm/_extends.esm.js';
import { isAxiosError } from 'axios';
import { OcrError, ErrorCategory } from '../errors/OcrError.esm.js';
import { fetchToken, extractDocumentWithAuth } from '../api/apiClient.esm.js';
import { AuthErrorCode, ErrorCodes, DEFAULT_REQUEST_TIMEOUT, ProcessingErrorCode } from '../constants/index.esm.js';
import { generateTraceId, validateFile } from '../utils/util.esm.js';

async function initializeSDK(params) {
    try {
        const { baseUrl, clientSecret, apiKey, clientId, resourceType } = params;
        const tokenData = await fetchToken(baseUrl, clientSecret, apiKey, clientId, resourceType);
        if (!(tokenData == null ? void 0 : tokenData.access_token)) {
            throw new Error('Failed to retrieve OCR authentication token');
        }
        const traceId = generateTraceId();
        return {
            token: tokenData.access_token,
            traceId
        };
    } catch (error) {
        let statusCode;
        let errorMessage = 'Failed to retrieve OCR authentication token';
        let errorDetails = {};
        let errorCode = AuthErrorCode.AUTH_FAILED;
        if (isAxiosError(error)) {
            var _error_response, _error_response_data, _error_response1, _error_config, _error_config1, _error_response2;
            statusCode = (_error_response = error.response) == null ? void 0 : _error_response.status;
            errorMessage = ((_error_response1 = error.response) == null ? void 0 : (_error_response_data = _error_response1.data) == null ? void 0 : _error_response_data.message) || error.message || errorMessage;
            errorDetails = {
                url: (_error_config = error.config) == null ? void 0 : _error_config.url,
                method: (_error_config1 = error.config) == null ? void 0 : _error_config1.method,
                data: (_error_response2 = error.response) == null ? void 0 : _error_response2.data
            };
            // Map HTTP status codes to our enum values
            switch(statusCode){
                case ErrorCodes.UNAUTHORIZED:
                    errorCode = AuthErrorCode.AUTH_ERROR_401;
                    break;
                case ErrorCodes.FORBIDDEN:
                    errorCode = AuthErrorCode.AUTH_ERROR_403;
                    break;
                case ErrorCodes.SERVER_ERROR:
                    errorCode = AuthErrorCode.AUTH_ERROR_500;
                    break;
                default:
                    errorCode = AuthErrorCode.AUTH_FAILED;
            }
        } else if (error instanceof Error) {
            errorMessage = error.message;
        }
        throw new OcrError({
            message: errorMessage,
            code: errorCode,
            statusCode,
            category: ErrorCategory.AUTHENTICATION,
            details: errorDetails,
            cause: error
        });
    }
}
/**
 * Extracts document data using OCR
 *
 * @param baseUrl - Base URL for the API
 * @param apiKey - API key for authentication
 * @param documentType - Type of document to extract (e.g., 'passport', 'id-card')
 * @param file - File to process
 * @param token - Authentication token from initializeSDK
 * @param traceId - Trace ID from initializeSDK
 * @param client - Client type (any string value, e.g., 'Mobile', 'Web', etc.)
 * @param abortController - Optional AbortController to cancel the request
 * @param timeoutMs - Optional timeout in milliseconds (default: DEFAULT_REQUEST_TIMEOUT)
 * @param num_pages - Optional number of pages to process from the document
 * @returns Promise resolving to extraction results
 * @throws ErrorResponse if extraction fails
 */ async function extractDocument(params) {
    try {
        var _data_result;
        const { baseUrl, token, traceId, apiKey, documentType, file, client, abortController, timeoutMs, num_pages } = params;
        validateFile(file);
        let controller = abortController;
        let timeoutId;
        if (!controller) {
            controller = new AbortController();
            // Use provided timeoutMs if available, otherwise use DEFAULT_REQUEST_TIMEOUT
            const timeout = timeoutMs || DEFAULT_REQUEST_TIMEOUT;
            // Set up timeout using the AbortController
            timeoutId = setTimeout(()=>{
                controller == null ? void 0 : controller.abort('OCR request timeout after ' + timeout + 'ms');
            }, timeout);
        }
        const result = await extractDocumentWithAuth(baseUrl, apiKey, documentType, file, token, traceId, client, controller, num_pages);
        // Clear the timeout if we created it
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        const { success = false, data = {}, aborted = false } = result;
        if (success !== true || !data || data.status !== 'success' && ((_data_result = data.result) == null ? void 0 : _data_result.status) !== 'success') {
            var _data_result1;
            // If request was aborted, use a specific error code
            const errorCode = aborted ? ProcessingErrorCode.OCR_REQUEST_ABORTED : ProcessingErrorCode.OCR_PROCESSING_FAILED;
            const errorMessage = aborted ? 'OCR extraction was aborted due to timeout or manual cancellation' : result.error || (((_data_result1 = data.result) == null ? void 0 : _data_result1.status) === 'Failed' ? 'Document processing failed' : 'OCR extraction failed');
            throw new OcrError({
                message: errorMessage,
                code: errorCode,
                statusCode: aborted ? ErrorCodes.REQUEST_TIMEOUT : ErrorCodes.EXTRACTION_FAILED,
                category: ErrorCategory.PROCESSING,
                details: _extends({}, data, {
                    aborted
                }),
                cause: data
            });
        }
        return result.data;
    } catch (error) {
        // If it's already an OcrError, just re-throw it
        if (error instanceof OcrError) {
            throw error;
        }
        let statusCode;
        let errorMessage = 'Failed to process document';
        let errorDetails = {};
        let errorCode = ProcessingErrorCode.OCR_PROCESSING_FAILED;
        if (isAxiosError(error)) {
            var _error_response, _error_response_data, _error_response1, _error_response_data1, _error_response2, _error_config, _error_config1, _error_response3;
            statusCode = (_error_response = error.response) == null ? void 0 : _error_response.status;
            errorMessage = ((_error_response1 = error.response) == null ? void 0 : (_error_response_data = _error_response1.data) == null ? void 0 : _error_response_data.detail) || ((_error_response2 = error.response) == null ? void 0 : (_error_response_data1 = _error_response2.data) == null ? void 0 : _error_response_data1.message) || error.message || errorMessage;
            errorDetails = {
                url: (_error_config = error.config) == null ? void 0 : _error_config.url,
                method: (_error_config1 = error.config) == null ? void 0 : _error_config1.method,
                documentType: params.documentType,
                data: (_error_response3 = error.response) == null ? void 0 : _error_response3.data
            };
            // Map HTTP status codes to enum values
            switch(statusCode){
                case ErrorCodes.BAD_REQUEST:
                    errorCode = ProcessingErrorCode.OCR_ERROR_400;
                    break;
                case ErrorCodes.UNAUTHORIZED:
                    errorCode = ProcessingErrorCode.OCR_ERROR_401;
                    break;
                case ErrorCodes.FORBIDDEN:
                    errorCode = ProcessingErrorCode.OCR_ERROR_403;
                    break;
                case ErrorCodes.NOT_FOUND:
                    errorCode = ProcessingErrorCode.OCR_ERROR_404;
                    break;
                case ErrorCodes.UNPROCESSABLE:
                    errorCode = ProcessingErrorCode.OCR_ERROR_422;
                    break;
                case ErrorCodes.SERVER_ERROR:
                    errorCode = ProcessingErrorCode.OCR_ERROR_500;
                    break;
                default:
                    errorCode = ProcessingErrorCode.OCR_PROCESSING_FAILED;
            }
        } else if (error instanceof Error) {
            errorMessage = error.message;
        }
        throw new OcrError({
            message: errorMessage,
            code: errorCode,
            statusCode,
            category: ErrorCategory.PROCESSING,
            details: errorDetails,
            cause: error
        });
    }
}

export { extractDocument, initializeSDK };
